import { FlatList, Pressable, SxProp, useDripsyTheme, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback } from 'react';

import { CurrenciesIcon } from '@/features/earn/components';
import { EarnStackRoutes, EarnStackScreenProps } from '@/features/earn/earn.navigator';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useFocusEffect } from '@react-navigation/native';
import { ActivityIndicator, Platform } from 'react-native';
import { Icon } from '../icons/Icon';
import { UniInput } from '../inputs';
import { NativeModalView } from '../modal/nativeModal/NativeModalView';
import { useNativeModalView } from '../modal/nativeModal/useNativeModalView';
import { TextBody } from '../typography';
import { useAccountSelectorNative, WalletsByAccountLabelItem } from './useAccountSelectorNative';

export const AccountSelectorNativeModal: React.FC<EarnStackScreenProps<EarnStackRoutes.AccountSelect>> = ({ route }) => {
  const { asset, value, disabledForwardBrowser = false, onSelect } = route.params;

  const { handleBack, animatedStyle } = useNativeModalView({});

  const { displayWallets, walletsSortByAccountLabel, handleChangeSearchText, isLoading } = useAccountSelectorNative({ asset });

  const isWeb = Platform.OS === 'web';

  const onSelectWalletId = (walletId: string) => {
    onSelect(walletId);
    handleBack();
  };
  const { theme } = useDripsyTheme();

  // Disable forward browser button
  useFocusEffect(
    useCallback(() => {
      if (isWeb && disabledForwardBrowser) {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
      }
    }, [])
  );

  return (
    <NativeModalView onBack={handleBack} title={t('earn-invest::glossary.select-asset')} animatedStyle={animatedStyle} hasScroll={isWeb}>
      <View sx={{ paddingX: '$4', paddingY: '$3' }}>
        <UniInput
          prefixIcon="search"
          wrapperSx={{ flex: 1 }}
          sx={{ width: '100%' }}
          onValueChange={(value) => handleChangeSearchText(value || '')}
          placeholder={t('earn-invest::glossary.search-asset')}
        />
      </View>
      <View sx={{ height: isWeb ? 'auto' : '80%' }}>
        {!isLoading ? (
          displayWallets.length > 0 ? (
            <FlatList
              data={displayWallets}
              keyExtractor={(item) => keyGenerator('accountSelector')}
              renderItem={({ item, index }: { item: WalletsByAccountLabelItem; index: number }) => (
                <React.Fragment>
                  {(index == 0 || walletsSortByAccountLabel[index - 1]?.accountNumber !== item.accountNumber) && (
                    <View sx={{ paddingX: '$4', pt: '$3', pb: '$1' }}>
                      <TextBody secondary>
                        {item.accountLabel ? t(`earn-invest::glossary.${item.accountLabel}`) : item.accountName || ' - '}
                      </TextBody>
                    </View>
                  )}
                  <Pressable
                    sx={{
                      paddingX: '$4',
                      paddingY: '$3',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: value === item.id ? '$disable' : 'transparent'
                    }}
                    onPress={() => onSelectWalletId(item.id)}>
                    <CurrenciesIcon asset={item.asset} textSize="textBody" textBold={false} />
                    <TextBody>{formatAmountByDecimals({ value: item.balance, decimals: item.decimals, suffix: item.asset })}</TextBody>
                  </Pressable>
                </React.Fragment>
              )}
            />
          ) : (
            <View sx={{ flex: 1, height: '100%', paddingX: '$4', paddingY: '$3', justifyContent: 'center', alignItems: 'center' }}>
              <Icon name="listZeroState" height={60} width={60} />
              <TextBody bold sx={{ textAlign: 'center', marginTop: '$3' }}>
                {t('earn-invest::glossary.no-accounts-found')}
              </TextBody>
            </View>
          )
        ) : (
          <View sx={{ padding: '$5' }}>
            <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
          </View>
        )}
      </View>
    </NativeModalView>
  );
};

enum StyleName {
  container = 'container',
  content = 'content',
  header = 'header'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    backgroundColor: '$backgroundPrimary'
  },
  content: {
    height: '100%',
    backgroundColor: '$backgroundPrimary'
  },
  header: {}
};
